/**
 * HFDS — Headzflex Design System
 * Component: HF-015 Delivery Date Estimator
 * Version: 1.0.0
 *
 * Notes:
 * - Calculates business days from the visitor's current date.
 * - Saturdays and Sundays are excluded.
 * - Dates are estimates, not delivery guarantees.
 * - Public holidays are intentionally not calculated.
 */

(() => {
  "use strict";

  const ROOT_SELECTOR =
    "[data-hf-delivery-estimator]";

  class HFDeliveryEstimator {
    /**
     * @param {HTMLElement} root
     */
    constructor(root) {
      this.root = root;

      this.locale =
        root.dataset.locale ||
        document.documentElement.lang ||
        navigator.language ||
        "en-US";

      this.cutoffHour = this.parseInteger(
        root.dataset.cutoffHour,
        14
      );

      this.economyMinimum = this.parseInteger(
        root.dataset.economyMin,
        6
      );

      this.economyMaximum = this.parseInteger(
        root.dataset.economyMax,
        9
      );

      this.expressMinimum = this.parseInteger(
        root.dataset.expressMin,
        2
      );

      this.expressMaximum = this.parseInteger(
        root.dataset.expressMax,
        3
      );

      this.economyElement = root.querySelector(
        "[data-hf-economy-date]"
      );

      this.expressElement = root.querySelector(
        "[data-hf-express-date]"
      );

      this.initialize();
    }

    initialize() {
      this.render();

      this.root.dataset.hfInitialized = "true";
    }

    /**
     * @param {string|undefined} value
     * @param {number} fallback
     * @returns {number}
     */
    parseInteger(value, fallback) {
      const parsed = Number.parseInt(
        String(value || ""),
        10
      );

      return Number.isFinite(parsed)
        ? parsed
        : fallback;
    }

    /**
     * @returns {Date}
     */
    getShippingStartDate() {
      const now = new Date();
      const start = new Date(now);

      start.setHours(12, 0, 0, 0);

      const afterCutoff =
        now.getHours() >= this.cutoffHour;

      if (afterCutoff) {
        start.setDate(start.getDate() + 1);
      }

      while (!this.isBusinessDay(start)) {
        start.setDate(start.getDate() + 1);
      }

      return start;
    }

    /**
     * @param {Date} date
     * @returns {boolean}
     */
    isBusinessDay(date) {
      const day = date.getDay();

      return day !== 0 && day !== 6;
    }

    /**
     * @param {Date} startDate
     * @param {number} businessDays
     * @returns {Date}
     */
    addBusinessDays(startDate, businessDays) {
      const date = new Date(startDate);
      let added = 0;

      while (added < businessDays) {
        date.setDate(date.getDate() + 1);

        if (this.isBusinessDay(date)) {
          added += 1;
        }
      }

      return date;
    }

    /**
     * @param {Date} startDate
     * @param {number} minimum
     * @param {number} maximum
     * @returns {{minimumDate: Date, maximumDate: Date}}
     */
    calculateRange(startDate, minimum, maximum) {
      return {
        minimumDate: this.addBusinessDays(
          startDate,
          minimum
        ),
        maximumDate: this.addBusinessDays(
          startDate,
          maximum
        )
      };
    }

    /**
     * @param {Date} date
     * @returns {string}
     */
    formatDate(date) {
      try {
        return new Intl.DateTimeFormat(
          this.locale,
          {
            weekday: "short",
            month: "short",
            day: "numeric"
          }
        ).format(date);
      } catch (error) {
        return new Intl.DateTimeFormat(
          "en-US",
          {
            weekday: "short",
            month: "short",
            day: "numeric"
          }
        ).format(date);
      }
    }

    /**
     * @param {Date} minimumDate
     * @param {Date} maximumDate
     * @returns {string}
     */
    formatRange(minimumDate, maximumDate) {
      return `${this.formatDate(
        minimumDate
      )} – ${this.formatDate(maximumDate)}`;
    }

    render() {
      const startDate =
        this.getShippingStartDate();

      const economyRange =
        this.calculateRange(
          startDate,
          this.economyMinimum,
          this.economyMaximum
        );

      const expressRange =
        this.calculateRange(
          startDate,
          this.expressMinimum,
          this.expressMaximum
        );

      if (this.economyElement) {
        this.economyElement.textContent =
          this.formatRange(
            economyRange.minimumDate,
            economyRange.maximumDate
          );

        this.economyElement.classList.remove(
          "is-loading"
        );
      }

      if (this.expressElement) {
        this.expressElement.textContent =
          this.formatRange(
            expressRange.minimumDate,
            expressRange.maximumDate
          );

        this.expressElement.classList.remove(
          "is-loading"
        );
      }

      document.dispatchEvent(
        new CustomEvent(
          "hfds:delivery-estimate:ready",
          {
            bubbles: true,
            detail: {
              economy: economyRange,
              express: expressRange
            }
          }
        )
      );
    }
  }

  function initializeDeliveryEstimators(
    context = document
  ) {
    document
      .querySelectorAll(ROOT_SELECTOR)
      .forEach((root) => {
        if (!(root instanceof HTMLElement)) {
          return;
        }

        if (
          context !== document &&
          !context.contains(root)
        ) {
          return;
        }

        if (
          root.dataset.hfInitialized === "true"
        ) {
          return;
        }

        root.hfDeliveryEstimator =
          new HFDeliveryEstimator(root);
      });
  }

  const ready = (callback) => {
    if (
      document.readyState === "loading"
    ) {
      document.addEventListener(
        "DOMContentLoaded",
        callback,
        {
          once: true
        }
      );

      return;
    }

    callback();
  };

  ready(() => {
    initializeDeliveryEstimators(document);
  });

  document.addEventListener(
    "shopify:section:load",
    (event) => {
      initializeDeliveryEstimators(
        event.target
      );
    }
  );

  window.HFDSDeliveryEstimator =
    Object.freeze({
      version: "1.0.0",
      initialize:
        initializeDeliveryEstimators
    });
})();